import React, { useState } from "react";
import { FileText, ChevronLeft, ChevronRight, X, Upload, AlertCircle } from "react-feather";
import { Input } from "reactstrap";
import styles from "./DocumentPreview.module.scss";
import { removeFile, uploadMultipleFiles, uploadSingleFile } from "../../../../api-handle/fileApi";
import { updateMerchantDetails } from "../../../../api-handle/merchantApi";
import { toast } from "react-toastify";

const FILE_BASE_URL = (process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL || "http://localhost:4001").replace(/\/?$/, "/");

const DocumentPreview = ({ merchant, setRefetch }) => {
    const [previewIndex, setPreviewIndex] = useState(0);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const [documents, setDocuments] = useState(merchant?.documents || []);
    const [contractDocument, setContractDocument] = useState(merchant?.contractDocument || "");
    const [newDocs, setNewDocs] = useState([]);
    const [newContract, setNewContract] = useState(null);

    const validDocuments = Array.isArray(documents)
        ? documents.filter((doc) => typeof doc === "string" && doc.trim() !== "")
        : [];

    const previewDocs = contractDocument
        ? [...validDocuments, contractDocument]
        : validDocuments;

    const handlePrev = () => {
        setPreviewIndex((prev) => (prev === 0 ? previewDocs.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setPreviewIndex((prev) => (prev === previewDocs.length - 1 ? 0 : prev + 1));
    };

    const handleRemoveDocument = async (index) => {
        try {
            await removeFile(documents[index]);
            setDocuments((prev) => prev.filter((_, i) => i !== index));
        } catch {
            toast.error("Failed to remove document");
        }
    };

    const handleRemoveContract = async () => {
        try {
            await removeFile(contractDocument);
            setContractDocument("");
        } catch {
            toast.error("Failed to remove contract document");
        }
    };

    const handleModalDocsUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploading(true);
        try {
            const uploaded = await uploadMultipleFiles(files);
            setNewDocs((prev) => [...prev, ...uploaded]);
        } catch {
            setError("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleModalContractUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const { fileName } = await uploadSingleFile(file);
            setNewContract(fileName);
        } catch {
            setError("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const removeNewDoc = (index) => {
        setNewDocs((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        setSaving(true);
        setError("");
        try {
            const payload = {};
            if (newDocs.length) payload.documents = [...documents, ...newDocs];
            if (newContract) payload.contractDocument = newContract;
            if (Object.keys(payload).length) {
                await updateMerchantDetails(payload);
                if (newDocs.length) setDocuments((prev) => [...prev, ...newDocs]);
                if (newContract) setContractDocument(newContract);
                toast.success("Documents updated successfully");
                resetModalState();
                setModalOpen(false);
                if (setRefetch) setRefetch((prev) => !prev);
            }
        } catch {
            setError("Failed to save changes");
            toast.error("Failed to save changes");
        } finally {
            setSaving(false);
        }
    };

    const resetModalState = () => {
        setNewDocs([]);
        setNewContract(null);
        setError("");
        setUploading(false);
    };

    const closeModal = () => {
        if (hasNewContent) {
            if (window.confirm("Discard unsaved changes?")) resetModalState();
            else return;
        }
        setModalOpen(false);
    };

    const hasNewContent = newDocs.length > 0 || newContract !== null;

    const DocCard = ({ doc, index, onRemove }) => {
        const filename = doc.split("/").pop();
        return (
            <div
                onClick={() => {
                    setPreviewIndex(index);
                    setPreviewOpen(true);
                }}
                style={{
                    cursor: "pointer",
                    width: 160,
                    minHeight: 200,
                    borderRadius: 8,
                    border: "1px solid #ddd",
                    boxShadow: "0 2px 6px rgb(0 0 0 / 0.1)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    padding: 15,
                    backgroundColor: "#fff",
                    position: "relative",
                    transition: "box-shadow 0.3s ease",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 4px 12px rgb(0 0 0 / 0.2)";
                    e.currentTarget.style.borderColor = "#33bdbf";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "0 2px 6px rgb(0 0 0 / 0.1)";
                    e.currentTarget.style.borderColor = "#ddd";
                }}
            >
                <div
                    style={{
                        flexGrow: 1,
                        width: "100%",
                        marginBottom: 12,
                        borderRadius: 6,
                        backgroundColor: "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        pointerEvents: "none",
                    }}
                >
                    <FileText size={64} color="#33bdbf" />
                </div>
                <span
                    style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#444",
                        textAlign: "center",
                        wordBreak: "break-word",
                        maxHeight: 40,
                        overflow: "hidden",
                        lineHeight: "1.2em",
                        marginBottom: 24,
                        display: "block",
                        pointerEvents: "none",
                    }}
                    title={filename}
                >
                    {filename}
                </span>
                {onRemove && (
                    <X
                        size={18}
                        style={{
                            position: "absolute",
                            bottom: 6,
                            right: 6,
                            cursor: "pointer",
                            color: "#e74c3c",
                            backgroundColor: "#fff",
                            borderRadius: "50%",
                            padding: 2,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove(index);
                        }}
                    />
                )}
            </div>
        );
    };

    return (
        <div className={styles.documentPreviewContainer}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h5 className={styles.sectionTitle} style={{ margin: 0 }}>Documents</h5>
                <button
                    onClick={() => setModalOpen(true)}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "10px 24px",
                        backgroundColor: "#33bdbf",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                    }}
                >
                    <Upload size={18} /> Upload Documents
                </button>
            </div>

            {/* Existing Documents */}
            {validDocuments.length > 0 && (
                <div className="mb-4">
                    <h6 style={{ fontWeight: 600, color: "#555", marginBottom: 12, fontSize: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        KYC Documents
                    </h6>
                    <div className="d-flex gap-3 flex-wrap">
                        {validDocuments.map((doc, i) => (
                            <DocCard key={i} doc={doc} index={i} onRemove={handleRemoveDocument} />
                        ))}
                    </div>
                </div>
            )}

            {/* Existing Contract Document */}
            {contractDocument && (
                <div className="mb-4">
                    <h6 style={{ fontWeight: 600, color: "#555", marginBottom: 12, fontSize: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Contract Document
                    </h6>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <DocCard doc={contractDocument} index={validDocuments.length} onRemove={(_i) => handleRemoveContract()} />
                    </div>
                </div>
            )}

            {!validDocuments.length && !contractDocument && (
                <p className={styles.noDocuments}>No documents uploaded yet</p>
            )}

            {/* Upload Modal */}
            {modalOpen && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1050,
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeModal();
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: 12,
                            width: "90%",
                            maxWidth: 600,
                            maxHeight: "85vh",
                            overflow: "auto",
                            padding: "28px 32px",
                            boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                            <h5 style={{ fontWeight: 700, color: "#222", margin: 0, fontSize: 18 }}>
                                Upload Documents
                            </h5>
                            <X
                                size={22}
                                style={{ cursor: "pointer", color: "#888" }}
                                onClick={closeModal}
                            />
                        </div>

                        {error && (
                            <div className="d-flex align-items-center text-danger small mb-3">
                                <AlertCircle size={16} className="me-1" /> {error}
                            </div>
                        )}

                        {/* KYC Documents */}
                        <div className="mb-4">
                            <label style={{ fontWeight: 600, fontSize: 14, color: "#333", marginBottom: 8, display: "block" }}>
                                KYC Documents <span style={{ color: "#e74c3c" }}>*</span>
                            </label>
                            <p style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>
                                Upload required identification and business documents (PDF, DOC, DOCX)
                            </p>
                            <label
                                htmlFor="modalDocsUpload"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "10px 20px",
                                    backgroundColor: "#f0f0f0",
                                    color: "#555",
                                    border: "2px dashed #ccc",
                                    borderRadius: 8,
                                    cursor: "pointer",
                                    fontSize: 14,
                                    fontWeight: 500,
                                    transition: "border-color 0.2s",
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.borderColor = "#33bdbf"}
                                onMouseLeave={(e) => e.currentTarget.style.borderColor = "#ccc"}
                            >
                                <Upload size={18} /> Choose Files
                            </label>
                            <Input
                                id="modalDocsUpload"
                                type="file"
                                multiple
                                accept=".pdf,.doc,.docx"
                                onChange={handleModalDocsUpload}
                                style={{ display: "none" }}
                                disabled={uploading}
                            />
                            {newDocs.length > 0 && (
                                <div className="mt-2 d-flex gap-2 flex-wrap">
                                    {newDocs.map((doc, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 6,
                                                padding: "4px 10px",
                                                backgroundColor: "#e8f4f4",
                                                borderRadius: 4,
                                                fontSize: 13,
                                                color: "#333",
                                            }}
                                        >
                                            <FileText size={14} color="#33bdbf" />
                                            {doc.split("/").pop().substring(0, 20)}...
                                            <X
                                                size={14}
                                                style={{ cursor: "pointer", color: "#e74c3c" }}
                                                onClick={() => removeNewDoc(i)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Contract Document */}
                        <div className="mb-4">
                            <label style={{ fontWeight: 600, fontSize: 14, color: "#333", marginBottom: 8, display: "block" }}>
                                Contract Document
                            </label>
                            <p style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>
                                Upload signed agreement or contract (PDF only)
                            </p>
                            <label
                                htmlFor="modalContractUpload"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "10px 20px",
                                    backgroundColor: "#f0f0f0",
                                    color: "#555",
                                    border: "2px dashed #ccc",
                                    borderRadius: 8,
                                    cursor: "pointer",
                                    fontSize: 14,
                                    fontWeight: 500,
                                    transition: "border-color 0.2s",
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.borderColor = "#33bdbf"}
                                onMouseLeave={(e) => e.currentTarget.style.borderColor = "#ccc"}
                            >
                                <Upload size={18} /> Choose File
                            </label>
                            <Input
                                id="modalContractUpload"
                                type="file"
                                accept="application/pdf,.pdf"
                                onChange={handleModalContractUpload}
                                style={{ display: "none" }}
                                disabled={uploading}
                            />
                            {newContract && (
                                <div className="mt-2 d-flex align-items-center gap-2">
                                    <FileText size={16} color="#33bdbf" />
                                    <span style={{ fontSize: 13, color: "#333" }}>
                                        {newContract.split("/").pop()}
                                    </span>
                                    <X
                                        size={16}
                                        style={{ cursor: "pointer", color: "#e74c3c" }}
                                        onClick={() => setNewContract(null)}
                                    />
                                </div>
                            )}
                        </div>

                        {uploading && (
                            <p style={{ color: "#888", fontStyle: "italic", fontSize: 13 }}>Uploading files...</p>
                        )}

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 20 }}>
                            <button
                                onClick={closeModal}
                                disabled={saving}
                                style={{
                                    padding: "10px 24px",
                                    backgroundColor: "#fff",
                                    color: "#555",
                                    border: "1px solid #ddd",
                                    borderRadius: 6,
                                    fontSize: 14,
                                    fontWeight: 500,
                                    cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving || !hasNewContent}
                                style={{
                                    padding: "10px 24px",
                                    backgroundColor: hasNewContent ? "#33bdbf" : "#ccc",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 6,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    cursor: hasNewContent && !saving ? "pointer" : "not-allowed",
                                }}
                            >
                                {saving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {previewOpen && previewDocs.length > 0 && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.7)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1100,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: 10,
                            width: "80vw",
                            maxWidth: 900,
                            maxHeight: "85vh",
                            padding: 20,
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <X
                            onClick={() => setPreviewOpen(false)}
                            style={{
                                position: "absolute",
                                top: 15,
                                right: 15,
                                cursor: "pointer",
                                color: "#555",
                            }}
                        />
                        <h5 style={{ marginBottom: 10, textAlign: "center", fontWeight: 600, color: "#222" }}>
                            Document Preview
                        </h5>
                        <iframe
                            src={`${FILE_BASE_URL}/${previewDocs[previewIndex]}`}
                            title="Document Preview"
                            style={{
                                border: "none",
                                borderRadius: 6,
                                width: "100%",
                                aspectRatio: "16 / 9",
                                marginBottom: 15,
                            }}
                        ></iframe>
                        <div style={{ display: "flex", justifyContent: "center", gap: 40, alignItems: "center" }}>
                            <ChevronLeft
                                size={32}
                                onClick={handlePrev}
                                style={{ cursor: "pointer", color: "#33bdbf" }}
                            />
                            <span style={{ color: "#888", fontSize: 14 }}>
                                {previewIndex + 1} / {previewDocs.length}
                            </span>
                            <ChevronRight
                                size={32}
                                onClick={handleNext}
                                style={{ cursor: "pointer", color: "#33bdbf" }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentPreview;
